import { ModalWindow, UIHeading2 } from '@cian/ui-kit';
import { LoadableErrorBoundary } from '../../../../../packages/LoadableHelpers';

import { useApplicationContext } from 'shared/utils/applicationContext';
import { FC, useCallback } from 'react';
import { useAppDispatch, useJsonQueryBackup } from 'shared/hooks';
import { applyFilters } from 'shared/thunks/filters';
import { updateLastAppliedFilter } from 'shared/slices/searchMetaTooltip';
import { updateTags } from 'shared/thunks/tags';
import { useSelector } from 'react-redux';
import { setJsonQuery } from 'shared/slices/jsonQuery';
import { updateSearchMeta } from 'shared/thunks/searchMeta';
import { selectJsonQuery } from 'shared/selectors/jsonQuery';
import { getUncommonProperties } from 'shared/utils/jsonQuery';
import { AllFiltersContentContainerLoadable } from '../AllFiltersContentContainer/AllFiltersContentContainerLoadable';
import { AllFiltersModalFooterContainer } from '../AllFiltersModalFooterContainer/AllFiltersModalFooterContainer';
import { resetTerms } from '../../../../../packages/JsonQuery/resetTerms';
import { dispatchAdvancedFiltersEvent } from 'shared/utils/event';

interface IAllFiltersModalContainerProps {
  onClose: VoidFunction;
}

export const AllFiltersModalContainer: FC<IAllFiltersModalContainerProps> = ({ onClose }) => {
  const { logger } = useApplicationContext();

  const dispatch = useAppDispatch();
  const jsonQuery = useSelector(selectJsonQuery);
  const { resetJsonQuery } = useJsonQueryBackup();

  const handleClose = useCallback(() => {
    onClose();
    resetJsonQuery();
    dispatch(updateLastAppliedFilter(null));
  }, [dispatch, onClose, resetJsonQuery]);

  const handleFiltersApply = useCallback(() => {
    onClose();
    dispatch(updateTags());
    dispatch(applyFilters());
    dispatchAdvancedFiltersEvent();
  }, [dispatch, onClose]);

  const handleFiltersReset = useCallback(() => {
    const uncommonProperties = getUncommonProperties(jsonQuery) || [];
    const nextJsonQuery = resetTerms(jsonQuery)(uncommonProperties);

    dispatch(setJsonQuery(nextJsonQuery));
    dispatch(updateSearchMeta());
  }, [dispatch, jsonQuery]);

  const handleRetry = () => AllFiltersContentContainerLoadable.load();

  return (
    <ModalWindow
      open
      onClose={handleClose}
      size="M"
      fixed
      width={952}
      header={<UIHeading2>Все фильтры</UIHeading2>}
      footer={
        <AllFiltersModalFooterContainer onFiltersReset={handleFiltersReset} onFiltersApply={handleFiltersApply} />
      }
    >
      <LoadableErrorBoundary logger={logger} onRetry={handleRetry}>
        <AllFiltersContentContainerLoadable />
      </LoadableErrorBoundary>
    </ModalWindow>
  );
};
